import { User } from '@cnx-saas/common';
import { genericId } from '@cnx-saas/utils-cnx-id';
import { ShipmentAssignmentPayload } from './ShipmentAssignment';
import { ShipmentDriver } from './ShipmentDriver';
import { ShipmentStatus } from './ShipmentStatus';
import { ShipmentStopCompletion } from './ShipmentStopCompletion';
import { ShipmentStopEstimatedTimes } from './ShipmentStopEstimatedTimes';
import { ShipmentStopStatus } from './ShipmentStopStatus';

export enum ShipmentEventName {
  SHIPMENT_STATUS_CREATED = 'SHIPMENT_STATUS_CREATED',
  SHIPMENT_STATUS_REQUESTED = 'SHIPMENT_STATUS_REQUESTED',
  SHIPMENT_STATUS_ALLOCATED = 'SHIPMENT_STATUS_ALLOCATED',
  SHIPMENT_STATUS_CANCELLED = 'SHIPMENT_STATUS_CANCELLED',
  SHIPMENT_STATUS_IN_TRANSIT = 'SHIPMENT_STATUS_IN_TRANSIT',
  SHIPMENT_STATUS_DELAYED = 'SHIPMENT_STATUS_DELAYED',
  SHIPMENT_STATUS_COMPLETED = 'SHIPMENT_STATUS_COMPLETED',
  SHIPMENT_STATUS_DELIVERED = 'SHIPMENT_STATUS_DELIVERED',
  SHIPMENT_STOPS_ESTIMATED_TIMES_UPDATED = 'SHIPMENT_STOPS_ESTIMATED_TIMES_UPDATED',
  SHIPMENT_INCIDENT_REPORTED = 'SHIPMENT_INCIDENT_REPORTED',
  SHIPMENT_STOP_STATUS_CANCELLED = 'SHIPMENT_STOP_STATUS_CANCELLED',
  SHIPMENT_STOP_STATUS_IN_PROCESS = 'SHIPMENT_STOP_STATUS_IN_PROCESS',
  SHIPMENT_CARRIER_UNASSIGNED = 'SHIPMENT_CARRIER_UNASSIGNED',
  SHIPMENT_STOP_STATUS_COMPLETED = 'SHIPMENT_STOP_STATUS_COMPLETED',
  SHIPMENT_STOP_ACTUAL_ARRIVAL_TIME_UPDATED = 'SHIPMENT_STOP_ACTUAL_ARRIVAL_TIME_UPDATED',
  SHIPMENT_DRIVER_UPDATED = 'SHIPMENT_DRIVER_UPDATED',
  SHIPMENT_STATUS_REJECTED = 'SHIPMENT_STATUS_REJECTED',
  SHIPMENT_STATUS_TIMEOUT = 'SHIPMENT_STATUS_TIMEOUT',
}

export class BaseShipmentEventLog {
  id: string;
  userId: string;
  tenantId: string;
  timestamp: Date;

  constructor(user: User) {
    this.id = genericId();
    this.timestamp = new Date();
    this.userId = user.userId;
    this.tenantId = user.tenantId;
  }
}

export class ShipmentStatusCreatedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_CREATED;
  readonly status = ShipmentStatus.CREATED;
}

export class ShipmentStatusRequestedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_REQUESTED;
  readonly status = ShipmentStatus.REQUESTED;
  request: ShipmentAssignmentPayload;
}

export class ShipmentStatusAllocatedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_ALLOCATED;
  readonly status = ShipmentStatus.ALLOCATED;
  assignment: ShipmentAssignmentPayload;
}

export class ShipmentStatusCancelledEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_CANCELLED;
  readonly status = ShipmentStatus.CANCELLED;
  optionalComment?: string;
}

export class ShipmentStatusInTransitEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_IN_TRANSIT;
  readonly status = ShipmentStatus.IN_TRANSIT;
}

export class ShipmentStatusDelayedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_DELAYED;
  readonly status = ShipmentStatus.DELAYED;
}

export class ShipmentStatusCompletedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_COMPLETED;
  readonly status = ShipmentStatus.COMPLETED;
}

export class ShipmentStatusRejectedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_REJECTED;
  readonly status = ShipmentStatus.REJECTED;
}

export class ShipmentStatusDeliveredEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_DELIVERED;
  readonly status = ShipmentStatus.DELIVERED;
}

export class ShipmentStopsEstimatedTimesUpdatedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STOPS_ESTIMATED_TIMES_UPDATED;
  optionalStopId?: string;
  estimatedTimes?: ShipmentStopEstimatedTimes;
}

export class ShipmentStopActualArrivalTimeUpdatedEventLog extends BaseShipmentEventLog {
  readonly eventName =
    ShipmentEventName.SHIPMENT_STOP_ACTUAL_ARRIVAL_TIME_UPDATED;
  stopId: string;
  actualArrivalTime: Date;
  optionalComment?: string;
}

export class ShipmentIncidentReportedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_INCIDENT_REPORTED;
  comment: string;
  optionalStopId?: string;
}

export class ShipmentStopStatusCancelledEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STOP_STATUS_CANCELLED;
  readonly stopStatus = ShipmentStopStatus.CANCELLED;
  stopId: string;
  optionalComment?: string;
}

export class ShipmentStopStatusCompletedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STOP_STATUS_COMPLETED;
  readonly stopStatus = ShipmentStopStatus.COMPLETED;
  stopId: string;
  completion: ShipmentStopCompletion;
}

export class ShipmentCarrierUnassignedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_CARRIER_UNASSIGNED;
  readonly status = ShipmentStatus.CREATED;
}

export class ShipmentDriverUpdatedEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_DRIVER_UPDATED;
  driver: ShipmentDriver;
}

export class ShipmentStatusTimeoutEventLog extends BaseShipmentEventLog {
  readonly eventName = ShipmentEventName.SHIPMENT_STATUS_TIMEOUT;
  readonly status = ShipmentStatus.TIMEOUT;
  request: ShipmentAssignmentPayload;
}

export type ShipmentEventLog =
  | ShipmentStatusCreatedEventLog
  | ShipmentStatusRequestedEventLog
  | ShipmentStatusAllocatedEventLog
  | ShipmentStatusCancelledEventLog
  | ShipmentStatusInTransitEventLog
  | ShipmentStatusDelayedEventLog
  | ShipmentStatusCompletedEventLog
  | ShipmentStatusDeliveredEventLog
  | ShipmentStopsEstimatedTimesUpdatedEventLog
  | ShipmentIncidentReportedEventLog
  | ShipmentStopStatusCancelledEventLog
  | ShipmentCarrierUnassignedEventLog
  | ShipmentStopStatusCompletedEventLog
  | ShipmentStopActualArrivalTimeUpdatedEventLog
  | ShipmentDriverUpdatedEventLog;
